import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Eye, RefreshCw, Swords } from 'lucide-react';
import type { LobbyTableInfo } from '@draughts/shared';
import { useOnlineStore, type OnlineUser } from '../../store/online-store';
import { useAuthStore } from '../../store/auth-store';
import { useLobbyStore } from '../../store/lobby-store';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { useSenzaRete } from '../../hooks/useSenzaRete';
import AvvisoSenzaRete from '../shared/AvvisoSenzaRete';
import { UserAvatar } from '../game/UserAvatar';
import { getOnlineUsers, subscribeSocketConnected } from '../../services/socket';
import { ratingBucketKey, type RatingBucket } from '../../utils/rating-bucket';
import { timbraPunteggi } from '../../utils/timbro-punteggi';
import { codiceErrore } from '../../utils/codice-errore';
import { chiaveErroreOnline } from '../../utils/chiave-errore-online';
import { getRatingClass } from '../../utils/rating';
import { variantShortLabel } from '../../utils/variant-labels';
import { variantPill } from '../../utils/variant-styling';
import { cadenceIcon, parseTimeControl } from '../../utils/cadence-icons';
import { isGuestPlayer } from '../../utils/guest';
import { distanzaDalBucket, mappaTavoliPerHost, tavoloDi } from '../../utils/tavolo-aperto';
import { righeOnline } from '../../utils/coppie-online';
import { ONLINE_USERS_REFRESH_MS, RITARDO_CAMBIO_BUCKET_MS } from '../../constants/timing';
import './OnlinePlayersList.css';

const REFRESH_SECONDS = ONLINE_USERS_REFRESH_MS / 1000;

/** Quanto si aspetta prima di dare un punteggio a chi e' appena entrato. */
const RITARDO_ARRIVO_MS = 1500;

/** Durata del lampo del bottone della riga: il gemello di `player-action-pulse`
 *  in OnlinePlayersList.css. Se cambia la', cambia anche qui — la classe va tolta
 *  quando l'animazione e' finita, non prima. */
const MS_LAMPO = 1200;

interface OnlinePlayersListProps {
  onChallengePlayer: (user: OnlineUser) => void;
  /**
   * Watch the live game of a player who is busy playing. Optional: without it
   * a "playing" row stays the dead end it used to be.
   */
  onSpectatePlayer?: (user: OnlineUser) => void;
  /**
   * Apre il profilo di un giocatore registrato: la porta e' il blocco di sinistra
   * della riga — avatar, nome, punteggio — come in ogni altra lista dell'app.
   *
   * Opzionale, e senza di lei quel blocco resta un `<div>` muto. La passa il pannello
   * dei collegati, dove la riga non deve scegliere niente. NON la passa il selettore
   * di Sfida diretta: li' premere un nome sceglie quell'avversario, e un profilo che
   * si frappone e' esattamente cio' che quel pannello non deve fare. Se arrivassero
   * tutte e due, `selectionMode` vince — vedi `portaProfilo`.
   *
   * Mai chiamata per un ospite (e quindi per un bot): il suo profilo e' una pagina
   * vuota con un punteggio predefinito che non vuol dire niente.
   */
  onViewProfile?: (user: OnlineUser) => void;
  selectedUserId?: string;
  /**
   * Nasconde il badge col totale: chi ospita la lista lo mostra già nel proprio
   * titolo, e ripeterlo a due righe di distanza è solo rumore. Sfida diretta non
   * la passa — lì il badge è l'unico posto dove quel numero compare.
   */
  hideCount?: boolean;
  /**
   * Di quale punteggio si parla: variante + cadenza.
   *
   * Senza, la lista si comporta come ha sempre fatto — il numero della cache di
   * presenza (blitz internazionale) e nessuna etichetta. Con, ogni riga porta il
   * punteggio di QUELLA variante e di QUELLA cadenza, e la testata lo dichiara:
   * un numero nudo accanto a un nome non dice a quale partita si riferisce.
   */
  ratingBucket?: RatingBucket;
  /**
   * La lista non e' una vetrina: e' un SELETTORE.
   *
   * Dentro Sfida diretta si sta scegliendo chi sfidare, e la riga di un giocatore
   * libero fa quello e basta — la si preme tutta intera. Sparisce il bottone
   * «Sfida», che li' non sfidava nessuno (sceglieva, e la sfida parte da «Invia»
   * due campi piu' sotto), e sparisce la porta del profilo: lo stesso gesto, a due
   * dita di distanza, non puo' fare due cose diverse. Il profilo di un giocatore
   * resta dove si va per guardarlo, cioe' il pannello dei collegati.
   *
   * Sparisce anche il chip «Punteggi: …» in testata: li' variante e cadenza si
   * scelgono due menu piu' sotto, nello stesso foglio, e la testata le ripeteva.
   * Prezzo dichiarato: quel chip era anche l'unica dichiarazione di QUALE colonna
   * stiano mostrando i numeri delle righe, nel caso in cui il server ripieghi su
   * un bucket diverso da quello chiesto.
   *
   * Le righe non selezionabili — chi gioca, chi e' «occupato», la riga unita di
   * una coppia — non cambiano: oggi non offrono «Sfida», e non devono diventare
   * bersagli.
   */
  selectionMode?: boolean;
  /**
   * Il riquadro che PRENDE IL POSTO della riga di quel giocatore.
   *
   * Chi ospita la lista decide cosa ci va dentro; la lista decide solo DOVE — e
   * il dove e' tutto il punto. La scelta «siediti al suo tavolo oppure sfidalo a
   * modo tuo» si disegnava in cima al pannello, cioe' spesso sopra la piega,
   * mentre il dito aveva premuto una riga a meta' elenco: si premeva «Sfida» e
   * non sembrava succedere niente.
   *
   * Il riquadro aperto non deve MAI essere cancellato dallo schermo finche' non
   * lo si chiude. Un «Siediti» rifiutato perche' il tavolo e' appena stato preso
   * lo fa ritirare anche lato server, e quel riquadro e' il posto dove sta
   * scritto PERCHE' il gesto e' fallito. Finche' stava fuori dalla lista era
   * gratis; qui dentro va garantito, ed e' il mestiere del `flatMap` che
   * costruisce le voci dell'elenco, piu' il riquadro ripetuto nei quattro rami
   * che non disegnano nessun elenco.
   *
   * Opzionale: Sfida diretta non la passa, e la' non cambia niente.
   */
  rigaEspansa?: { userId: string; contenuto: ReactNode } | null;
  /**
   * La testata la disegna il pannello che ospita la lista.
   *
   * Il pannello dei collegati ha gia' la sua — il titolo col totale accanto, in
   * `.play-online-head` — e due righe piu' sotto questa lista ne apriva una
   * seconda: un titolino che diceva cosa fare delle righe, il conto alla rovescia
   * e il chip dei punteggi, che si prende una riga intera sopra un elenco in cui
   * ogni riga porta gia' il suo numero. Due testate per un pannello solo, e da
   * affiancati la meta' destra della prima era vuota, perche' la' la X non c'e'.
   *
   * Con questa prop la lista non disegna nessuna `.players-header` — via l'intero
   * involucro e non i soli figli: col badge del totale spento da `hideCount` e il
   * titolino che ripeteva il titolo del pannello, una riga vuota col suo filo e il
   * suo margine si vede — e il conto alla rovescia lo porta in `conto` con un
   * portale, nella veste compatta.
   *
   * Il portale e non una callback: lo stato che batte e' suo (lo azzera
   * `caricaUtenti`, e quel battito al secondo e' anche cio' che fa ricadere i
   * tavoli scaduti dall'ordinamento — vedi `tavoliPerHost`), e risollevarlo nel
   * pannello vorrebbe dire un `setState` al secondo lassu', cioe' ridisegnare ogni
   * secondo anche il riquadro «siediti oppure sfidalo» e il fuoco che ci sta
   * dentro.
   *
   * `conto` puo' essere `null`: e' il nodo-slot del pannello, e arriva dal suo
   * `ref` un render dopo il primo. Per quel fotogramma il conto non si disegna da
   * nessuna parte, che e' meglio di disegnarlo due volte. Ed e' il motivo per cui
   * questa prop e' un oggetto e non un elemento nullabile: la presenza
   * dell'oggetto dice «la testata la disegna l'ospite», e un `null` nudo non
   * saprebbe distinguerlo da «nessun ospite» — la lista aprirebbe la propria
   * testata per un fotogramma, col titolino e il chip che lampeggiano.
   *
   * Il chip dei punteggi NON sparisce con la testata: resta, da solo, come riga
   * sopra l'elenco (`players-rating-bucket--sola`). Era il prezzo dichiarato di
   * questa prop, ed era troppo caro: quel chip e' l'unica dichiarazione di QUALE
   * colonna portino i numeri delle righe — otto per giocatore — e senza di lui
   * accanto a ogni nome resta un numero nudo. Da affiancati la variante la comanda
   * il menu «Gioca» di fianco, quindi la riga cambia sotto gli occhi di chi la
   * cambia, ed e' proprio allora che serve. Nella testata dell'ospite non ci sta:
   * le due testate della coppia sono alte uguali per contratto (PlayOverlay.css),
   * e una riga intera in piu' sfalserebbe il filo che le attraversa.
   *
   * Opzionale, e la passa solo il pannello dei collegati quando e' affiancato.
   * Sfida diretta non la passa: la' il titolino sta dentro un form e dice cosa
   * fare delle righe.
   */
  testataAltrove?: { conto: HTMLElement | null };
}

/**
 * Quanto e' vicina una partita con questa persona. E' la chiave con cui la
 * lista si ordina, e va dal subito al mai:
 *
 *   0  ha un tavolo aperto NELLA variante e cadenza che si stanno guardando
 *   1  ha un tavolo aperto in quella variante, ma a un'altra cadenza
 *   2  ha un tavolo aperto in un'altra variante
 *   3  e' libero: lo si puo' sfidare, e poi aspettare che risponda
 *   4  sta giocando (e `away` con lui: nemmeno quello offre un gesto)
 *
 * I primi tre scaglioni sono la stessa cosa — un tavolo aperto — ordinata per
 * quanto somiglia a cio' che chi guarda ha scelto: un tavolo frisone davanti a
 * uno italiano, mentre si guarda l'italiano, era il difetto da chiudere.
 *
 * Le condizioni sullo STATO sono le stesse di `getStatusBadge` qui sotto, nello
 * stesso ordine, e devono restarlo: l'ordine della lista e il colore del badge
 * sono due facce della stessa frase, e il giorno in cui divergessero la lista
 * metterebbe in cima una riga che si dipinge come irraggiungibile. Il
 * sotto-ordine fra i tavoli il badge non lo distingue — li dipinge tutti verdi,
 * ciascuno con la sua sigla — ed e' li' che si legge il perche' di quell'ordine.
 * Il test guarda apposta le CLASSI dei badge, non i nomi.
 */
function rangoGiocabilita(
  user: OnlineUser,
  tavolo: LobbyTableInfo | undefined,
  bucket: RatingBucket | undefined,
): number {
  if (user.status === 'playing') return 4;
  if (tavolo && user.status === 'available') return distanzaDalBucket(tavolo, bucket);
  return user.status === 'available' ? 3 : 4;
}

function OnlinePlayersList({ onChallengePlayer, onSpectatePlayer, onViewProfile, selectedUserId, hideCount, ratingBucket, selectionMode, rigaEspansa, testataAltrove }: OnlinePlayersListProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  /**
   * Il CODICE del guasto, non il suo messaggio.
   *
   * Qui c'era `err.message`, stampato tale e quale: «Not connected to server»,
   * una frase inglese scritta per i log, sotto gli occhi di chi aveva solo
   * l'app tornata dal secondo piano. Col codice si sceglie una traduzione — e
   * si distingue «mi sto ricollegando» da «e' andato storto qualcosa».
   */
  const [codiceGuasto, setCodiceGuasto] = useState<string | null>(null);
  const [secondsUntilReload, setSecondsUntilReload] = useState(REFRESH_SECONDS);
  /** Una richiesta e' gia' in volo: un socket che sbatte non deve fare raffiche. */
  const inVoloRef = useRef(false);

  /*
   * Quale riga sta lampeggiando.
   *
   * Uno stato solo per tutta la lista, e non uno per riga: le righe le rende
   * questo componente in un `flatMap`, non un componente per riga come
   * `GameRow`. Una alla volta basta — premere un'altra riga sposta la classe, e
   * l'animazione riparte da se' perche' quel nodo la classe non ce l'aveva.
   * Ripremere la stessa mentre lampeggia non fa ripartire niente, ed e' lo
   * stesso comportamento della game-row: la' il lampo riparte perche' la classe
   * CADE a fine animazione, non perche' il gestore la rimetta.
   */
  const [idLampo, setIdLampo] = useState<string | null>(null);
  const timerLampo = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timerLampo.current) clearTimeout(timerLampo.current); }, []);

  const { isAuthenticated } = useAuthStore();
  const { onlineUsers, setOnlineUsers, setCounts, setIsLoadingUsers } = useOnlineStore();
  /*
   * «Questa lista non descrive piu' niente di vivo.» La alza il multiplayer-store
   * — socket giu' da abbastanza, o dispositivo senza rete — e qui non si fa altro
   * che dirlo invece di mostrare la fotografia di prima.
   *
   * Non si guarda `status === 'disconnected'`: quello sfarfalla a ogni blip.
   */
  const presenzaScaduta = useMultiplayerStore((s) => s.presenzaScaduta);
  const senzaRete = useSenzaRete();

  const prossimaRicaricaRef = useRef(Date.now() + ONLINE_USERS_REFRESH_MS);

  /* Il bucket come stringa: una dipendenza di effetto che non cambia a ogni
     render solo perche' chi ospita ha ricostruito l'oggetto. */
  const chiaveBucket = ratingBucket ? ratingBucketKey(ratingBucket) : null;
  /** Il bucket che il SERVER ha onorato: è quello che il chip ha diritto di dire. */
  const [bucketMostrato, setBucketMostrato] = useState<RatingBucket | null>(null);
  const bucketRef = useRef(ratingBucket);
  bucketRef.current = ratingBucket;

  const caricaUtenti = useCallback(async () => {
    if (inVoloRef.current) return;
    inVoloRef.current = true;
    // Reset the countdown at the start of each refresh cycle
    prossimaRicaricaRef.current = Date.now() + ONLINE_USERS_REFRESH_MS;
    setSecondsUntilReload(REFRESH_SECONDS);
    try {
      setIsLoading(true);
      setIsLoadingUsers(true);
      const result = await getOnlineUsers(bucketRef.current);
      /* Il timbro: queste righe portano il punteggio del bucket che il server
         ha onorato. Le righe che arriveranno dal broadcast non ce l'hanno, ed e'
         cosi' che si riconoscono. */
      setOnlineUsers(timbraPunteggi(result.users, result.bucket));
      setBucketMostrato(result.bucket ?? null);
      if (result.counts) setCounts(result.counts);
      setCodiceGuasto(null);
    } catch (err) {
      setCodiceGuasto(codiceErrore(err) ?? 'SCONOSCIUTO');
    } finally {
      inVoloRef.current = false;
      setIsLoading(false);
      setIsLoadingUsers(false);
    }
  }, [setOnlineUsers, setCounts, setIsLoadingUsers]);

  // Fetch online users on mount and periodically
  useEffect(() => {
    void caricaUtenti();

    // Refresh the list every 30 seconds, ticking a countdown down to the next reload
    const fetchInterval = setInterval(() => { void caricaUtenti(); }, ONLINE_USERS_REFRESH_MS);
    const tickInterval = setInterval(() => {
      setSecondsUntilReload(Math.max(0, Math.ceil((prossimaRicaricaRef.current - Date.now()) / 1000)));
    }, 1000);
    return () => {
      clearInterval(fetchInterval);
      clearInterval(tickInterval);
    };
  }, [caricaUtenti]);

  /*
   * E appena il socket torna, si ricarica: non fra trenta secondi.
   *
   * L'intervallo qui sopra era l'unica cosa che rimetteva in piedi la lista, e
   * dura mezzo minuto. Il socket, invece, riconnette in uno-tre secondi: chi
   * rientrava dal secondo piano restava a guardare un guasto gia' finito per
   * tutto il resto del giro. Il riaggancio esisteva per lo spettatore e per
   * l'arena, non per questa lista.
   */
  useEffect(() => subscribeSocketConnected(() => { void caricaUtenti(); }), [caricaUtenti]);

  /*
   * E quando la presenza torna VALIDA, si rilegge subito.
   *
   * Il riaggancio qui sopra e' appeso al `connect` del socket, che su un buco di
   * rete breve non arriva mai: il socket non muore, quindi nessuno annuncia che
   * si puo' tornare a chiedere. Il risultato, visto nel browser e non a
   * tavolino: la pillola della hero tornava a «1 Online» mentre questa lista,
   * svuotata e senza piu' la bandiera che la spiegava, diceva «non c'e' nessun
   * altro online» — una bugia secca, per i quindici o venti secondi che
   * restavano al giro di ricarica.
   *
   * Il doppio giro sul percorso del `connect` non e' un problema: `caricaUtenti`
   * ha la sua guardia `inVoloRef`, ed e' la stessa che protegge dalle raffiche
   * di un socket che sbatte.
   */
  const eraScaduta = useRef(false);
  useEffect(() => {
    if (presenzaScaduta) { eraScaduta.current = true; return; }
    if (!eraScaduta.current) return;
    eraScaduta.current = false;
    void caricaUtenti();
  }, [presenzaScaduta, caricaUtenti]);

  /*
   * Cambia il bucket, cambiano i numeri — e non fra trenta secondi.
   *
   * Nella Sfida diretta variante e cadenza si scelgono mentre la lista e' sotto
   * gli occhi: senza questo, i punteggi resterebbero quelli della variante di
   * prima fino al giro di ricarica. Il ritardo e' per chi scorre il menu delle
   * varianti: si chiede una volta sola, non una per voce sfiorata.
   */
  const primoBucketRef = useRef(true);
  useEffect(() => {
    if (primoBucketRef.current) {
      primoBucketRef.current = false;
      return;
    }
    const t = setTimeout(() => { void caricaUtenti(); }, RITARDO_CAMBIO_BUCKET_MS);
    return () => clearTimeout(t);
  }, [chiaveBucket, caricaUtenti]);

  /*
   * Chi e' entrato adesso non ha un punteggio da mostrare.
   *
   * Il broadcast `online:update` e' uno per tutti e porta il numero della cache
   * di presenza: non puo' sapere quale variante stia guardando ciascun
   * destinatario. La riga entra senza timbro — e senza numero — e qui si chiede
   * la lista per dargliene uno giusto. Non e' un ciclo: la risposta timbra TUTTE
   * le righe, anche quelle per cui il punteggio non c'e'.
   */
  const senzaTimbro = chiaveBucket !== null
    && onlineUsers.some((u) => !isGuestPlayer(u) && u.ratingBucket === undefined);
  useEffect(() => {
    if (!senzaTimbro) return;
    const t = setTimeout(() => { void caricaUtenti(); }, RITARDO_ARRIVO_MS);
    return () => clearTimeout(t);
  }, [senzaTimbro, caricaUtenti]);

  /*
   * I tavoli aperti, per nome dell'host: e' cosi' che una riga sa di stare
   * aspettando un avversario. Si seleziona l'ARRAY dallo store e si costruisce
   * la mappa qui — un selettore che ritorna una Map nuova a ogni chiamata
   * darebbe un'identita' sempre diversa a zustand, cioe' un render per render.
   *
   * `secondsUntilReload` nelle dipendenze e' il battito al secondo che c'e'
   * gia': serve a far ricadere i tavoli scaduti anche quando la lista dei
   * tavoli non si muove (il loro ritiro arriva in broadcast, ma non all'istante
   * della scadenza).
   *
   * Sta PRIMA dell'ordinamento qui sotto perche' adesso lo serve: chi ha un
   * tavolo aperto sale.
   */
  const tables = useLobbyStore((s) => s.tables);
  const tavoliPerHost = useMemo(() => mappaTavoliPerHost(tables), [tables, secondsUntilReload]);

  /**
   * Due chiavi, in quest'ordine.
   *
   * 1. Prima i registrati, poi gli ospiti (e i bot, che qui sono ospiti). Il
   *    server manda la lista in ordine di connessione, quindi un avversario
   *    vero finiva sepolto sotto gli ospiti collegati dopo di lui.
   * 2. Poi quanto e' vicina una partita con quella persona: chi ha un tavolo
   *    aperto (un click e si gioca), poi chi e' libero (una sfida e si
   *    aspetta), in fondo chi sta gia' giocando. Il badge lo diceva gia' a
   *    colpo d'occhio, ma la riga non si muoveva: un tavolo aperto poteva
   *    finire sotto la piega mentre in cima stava chi non e' raggiungibile.
   * 3. E fra i tavoli aperti, per primi quelli della variante e della cadenza
   *    che si stanno guardando — le stesse del punteggio mostrato in testata.
   *    Vedi `rangoGiocabilita`.
   *
   * `sort` e' stabile: dentro ogni sotto-gruppo l'ordine di connessione resta,
   * ed e' l'unica cosa che tiene ferma una riga.
   *
   * Il prezzo, dichiarato: una riga PUO' spostarsi sotto il dito, quando quel
   * giocatore inizia una partita o ritira il tavolo. Non e' nuovo — lo stesso
   * evento cambia gia' il bottone da «Sfida» a «Guarda», e le righe entrano ed
   * escono dalla lista per conto loro — e congelare l'ordine mentre il
   * pannello e' aperto sarebbe peggio del male: si premerebbe «Siediti» su un
   * tavolo che non c'e' piu'.
   */
  const sortedUsers = useMemo(
    () => [...onlineUsers].sort((a, b) => (
      Number(isGuestPlayer(a)) - Number(isGuestPlayer(b))
      || rangoGiocabilita(a, tavoloDi(tavoliPerHost, a.username), ratingBucket)
         - rangoGiocabilita(b, tavoloDi(tavoliPerHost, b.username), ratingBucket)
    )),
    // `chiaveBucket` e non l'oggetto: chi ospita la lista lo ricostruisce a
    // ogni render, e una dipendenza per identita' rifarebbe il sort per niente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlineUsers, tavoliPerHost, chiaveBucket],
  );

  /*
   * Una partita = una riga.
   *
   * Passata di PRESENTAZIONE, e dopo l'ordinamento apposta: la chiave con cui
   * la lista si ordina non la tocca nessuno, e i due membri di una coppia sono
   * comunque di rango 4. Vedi `righeOnline` per la regola — nomina reciproca,
   * per nome, in minuscolo — e per il prezzo dichiarato sull'ordine.
   */
  const righe = useMemo(() => righeOnline(sortedUsers), [sortedUsers]);

  /*
   * Chiusa la scelta, il fuoco torna al «Sfida» di quella riga.
   *
   * Il bottone che si e' premuto si smonta insieme alla riga che il riquadro
   * sostituisce, quindi all'apertura il fuoco lo prende il riquadro (ci pensa chi
   * lo disegna) e alla chiusura va riportato a mano: senza, resta sul `<body>` e
   * chi naviga da tastiera ricomincia il giro dalla testata del pannello.
   *
   * La guardia sul `<body>` non e' prudenza di troppo: «Invia una sfida diversa»
   * chiude il riquadro E apre un altro foglio nello stesso istante, e un fuoco
   * rimesso qui glielo ruberebbe. Si rifocalizza solo se il fuoco non ce l'ha
   * nessuno. Gli hook stanno QUI e non in fondo: sotto ci sono cinque uscite
   * anticipate, e un hook dopo una di quelle e' il crash «Rendered more hooks
   * than during the previous render».
   */
  const radiceRef = useRef<HTMLDivElement>(null);
  const idEspansa = rigaEspansa?.userId ?? null;
  const idEspansaPrecedente = useRef<string | null>(null);
  useEffect(() => {
    const chiusa = idEspansaPrecedente.current;
    idEspansaPrecedente.current = idEspansa;
    if (!chiusa || idEspansa) return;
    if (document.activeElement !== document.body) return;
    radiceRef.current
      ?.querySelector<HTMLElement>(`.player-challenge-btn[data-userid="${chiusa}"]`)
      ?.focus();
  }, [idEspansa]);

  /*
   * Il badge parla solo quando ha qualcosa da dire, e le sue due notizie
   * arrivano da porte diverse: lo stato di presenza viaggia con la riga, il
   * tavolo aperto sta nella lista dei tavoli.
   *
   * Chi e' semplicemente libero non ha badge. «Disponibile» su ogni riga era il
   * caso normale ripetuto a ogni riga: rumore che a 360px toglieva spazio al
   * nome, al punteggio e al bottone. L'assenza di badge E' «disponibile».
   *
   * `playing` viene PRIMA del tavolo: fra l'inizio di una partita e l'arrivo di
   * `lobby:table-removed` c'e' un istante in cui l'host e' in partita e il suo
   * tavolo e' ancora nella lista del client. In partita vince sempre.
   *
   * Stesse condizioni, stesso ordine, di `rangoGiocabilita` — che e' la chiave
   * con cui la lista si ordina. Toccare una delle due senza l'altra significa
   * mettere in cima una riga dipinta come irraggiungibile.
   */
  const getStatusBadge = (user: OnlineUser, tavolo: LobbyTableInfo | undefined) => {
    if (user.status === 'playing') {
      /* Il nome dell'avversario puo' mancare (stanza non ancora nata, annuncio
         di un percorso che non lo porta): allora si dice «In partita» e basta,
         e il giro successivo della lista lo riempie. */
      return (
        <span className="status-badge playing">
          {user.opponent
            ? t('online.status.playingWith', { username: user.opponent })
            : t('online.status.playing')}
        </span>
      );
    }
    /*
     * Verde, come «libero», perche' dice una cosa ancora migliore: si puo'
     * giocare con lui SUBITO, sedendosi al tavolo che ha gia' aperto. E dice a
     * cosa — bandiera e sigla, la stessa coppia delle righe delle partite.
     *
     * La condizione e' `available` e non «qualunque cosa diversa da playing»:
     * e' la stessa che usa la colonna delle azioni qui sotto. Un `away` con un
     * tavolo in lista li' riceve l'etichetta inerte «occupato», e un badge
     * verde accanto prometterebbe un gesto che la riga non offre.
     */
    if (tavolo && user.status === 'available') {
      const pastiglia = variantPill(tavolo.variant);
      return (
        <span className="status-badge waiting">
          {t('online.status.waiting')}
          {pastiglia.flag}
          {/* La sigla si vede e non si legge: questo badge finisce dentro il
              nome accessibile del bottone del profilo, e li' «ITA» dice meno
              del nome intero della variante. */}
          <span aria-hidden="true">{pastiglia.code}</span>
          <span className="player-sr">{variantShortLabel(t, tavolo.variant)}</span>
        </span>
      );
    }
    if (user.status === 'away') {
      return <span className="status-badge away">{t('online.status.away')}</span>;
    }
    return null;
  };

  /*
   * La pastiglia della riga unita: non «con chi» — i due nomi sono li' accanto —
   * ma SU COSA.
   *
   * Stessa forma rossa di `.status-badge.playing`, quindi la riga continua a
   * dipingersi come irraggiungibile ed e' ancora quella classe a legare il
   * colore all'ordine (vedi `rangoGiocabilita`). E stessa ricetta del badge
   * verde «Aspetta» qui sopra: la sigla si vede e non si legge, il nome intero
   * della variante lo riceve chi ascolta.
   *
   * Senza `game` si torna a «Gioca» e basta: una riga passata a `playing` per
   * annuncio non porta la partita, e il giro successivo della lista gliela da'.
   */
  const badgePartita = (game: OnlineUser['game']) => {
    const tc = game ? parseTimeControl(game.timeControl) : null;
    if (!game || !tc) {
      return <span className="status-badge playing">{t('online.status.playing')}</span>;
    }
    const pastiglia = variantPill(game.variant);
    return (
      <span className="status-badge playing">
        {pastiglia.flag}
        <span aria-hidden="true">{pastiglia.code}</span>
        <span className="player-sr">{variantShortLabel(t, game.variant)}</span>
        {cadenceIcon(tc.category, 12)}
        {/* Senza orologio non c'e' un numero da mostrare: l'infinito dice tutto,
            e la frase intera («Senza Limite di Tempo») in una pastiglia da 0,7rem
            manderebbe a capo la riga. Per chi ascolta c'e' comunque. */}
        {tc.category === 'none'
          ? <span className="player-sr">{t('game.timeControls.noTimeLimit')}</span>
          : (
            <>
              {tc.clock}
              <span className="player-sr">{t(`leaderboard.${tc.category}`)}</span>
            </>
          )}
      </span>
    );
  };

  /*
   * Il vuoto in mezzo alla riga: INDICA il bottone, non lo preme.
   *
   * E' il gesto della `.game-row` di partite recenti — premere la riga fa
   * lampeggiare il pulsante «Replay» — portato qui, dove serviva ancora di piu':
   * sul telefono non esiste nessun passaggio del puntatore che accenda il bottone,
   * quindi senza il lampo quella fascia di riga non rispondeva affatto.
   *
   * Cosa NON fa: sfidare. La riga non e' un bersaglio e non lo diventa adesso —
   * i suoi due terzi di sinistra aprono un profilo, e lo stesso gesto a due dita
   * di distanza non puo' avere due esiti. Qui si accende una luce, e basta.
   *
   * Due guardie, e tutte e due chiedono al DOM invece di rifare i conti del
   * render:
   *  1. dentro un bottone non si lampeggia — la porta del profilo, «Sfida» e
   *     «Guarda» hanno gia' il loro esito, e indicarlo mentre lo si esegue e'
   *     rumore. Vale anche per il selettore di Sfida diretta, dove la riga INTERA
   *     e' un bottone: non serve nessun caso particolare;
   *  2. niente lampo dove non c'e' niente da indicare: una riga «Occupato», o una
   *     gia' scelta (che al posto del bottone mostra la spunta), non ha nessun
   *     bottone da accendere, e un lampo prometterebbe un gesto inesistente. E' la
   *     stessa regola del «niente lampo senza `onSelect`» della game-row.
   * Chiedendolo al DOM, le guardie non possono divergere da cio' che la riga ha
   * disegnato davvero.
   */
  const indicaAzione = (e: MouseEvent<HTMLLIElement>, idRiga: string) => {
    if ((e.target as HTMLElement).closest('button')) return;
    if (!e.currentTarget.querySelector('.player-challenge-btn, .player-watch-btn')) return;
    setIdLampo(idRiga);
    if (timerLampo.current) clearTimeout(timerLampo.current);
    timerLampo.current = setTimeout(() => setIdLampo(null), MS_LAMPO);
  };

  /**
   * Questa riga SCEGLIE, invece di offrire azioni.
   *
   * Una funzione e non due condizioni gemelle: da qui dipendono la forma della
   * porta (un bottone che prende tutta la riga) e l'assenza della colonna delle
   * azioni, e se le due frasi divergessero si otterrebbe una riga che si preme
   * tutta E un bottone «Sfida» dentro di lei.
   *
   * `available` e' la stessa condizione che decideva il bottone «Sfida», e tiene
   * fuori da sola le righe unite: `coppie-online.ts` accoppia solo due righe
   * entrambe in `playing`.
   */
  const rigaSceglie = (user: OnlineUser) => Boolean(selectionMode) && user.status === 'available';

  /*
   * Il blocco di sinistra: avatar, nome, punteggio, e in coda il `badge` che il
   * chiamante decide. E' la porta del profilo quando c'e' un profilo da aprire, ed
   * e' la riga che sceglie quando la lista e' un selettore: una porta sola, mai due.
   *
   * Estratta perche' adesso ce ne vogliono DUE sulla stessa riga — i due
   * giocatori di una coppia — ma la forma non e' cambiata di una virgola: il
   * test che legge questo file dal disco pretende le stringhe esatte di questi
   * tre nodi (`nome-apre-il-profilo.test.ts`), e il parametro deve chiamarsi
   * `user`. Il nome accessibile viene dallo `<span>` nascosto e NON da un
   * `aria-label`: quello sostituirebbe il contenuto, e chi ascolta perderebbe
   * il punteggio e lo stato.
   *
   * `title`: nella riga unita il nome si tronca (vedi il foglio), e un nome
   * troncato che e' una porta deve restare identificabile. Stessa scelta, e
   * stessa riga di codice, di `button.game-row__who`.
   */
  const portaProfilo = (user: OnlineUser, badge: ReactNode) => {
    const sceglibile = rigaSceglie(user);
    /* Profilo solo per i registrati: quello di un ospite è una pagina vuota,
       e per un bot rivelerebbe il travestimento. */
    const puoProfilo = Boolean(onViewProfile) && !isGuestPlayer(user);
    /*
     * E il selettore VINCE sul profilo.
     *
     * Non e' un caso teorico: chi monta la lista come selettore oggi non passa
     * `onViewProfile`, ma passarli tutti e due e' un errore che si fa in un
     * minuto, e il risultato sarebbe la cosa che questo lavoro e' venuto a
     * togliere — premere un nome e ritrovarsi un profilo invece dell'avversario
     * scelto. Una porta sola per riga, decisa qui.
     */
    const apreProfilo = puoProfilo && !sceglibile;
    const contenutoInfo = (
      <>
        {/* Il nome accessibile del bottone: dice «Profilo, Ada, 1450» quando Ada
            e' libera, e «… In partita con Bo» o «… In attesa, Italiana 8×8»
            quando c'e' un badge. Stessa ricetta e stessa chiave di GameRow.
            Nel selettore il verbo e' un altro — «Sfida, Ada, 1450» — e la chiave
            e' quella che portava il bottone sparito. */}
        {sceglibile && <span className="player-sr">{t('online.challenge.challenge')}</span>}
        {apreProfilo && <span className="player-sr">{t('online.viewProfile')}</span>}
        <UserAvatar username={user.username} avatar={user.avatar} variant="opponent" size="sm" />
        <span className="player-name">{user.username}</span>
        {/* Un ospite (e quindi un bot) non ha punteggio: il server ora
            manda `null`, e il prefisso `Guest_` resta come rete di
            sicurezza per i client su un bundle in cache. Stessa regola
            già applicata in PlayerCard / GameEndOverlay / GameReplay.

            Il timbro è la terza condizione: quando si sta mostrando il
            punteggio di una variante precisa, una riga arrivata dal
            broadcast porta il numero di un'altra colonna. Meglio nessun
            numero per un secondo che il numero sbagliato sotto
            un'etichetta che ne promette un altro. */}
        {!isGuestPlayer(user) && user.rating != null
          && (chiaveBucket === null || user.ratingBucket !== undefined) && (
          <span className={`player-rating ${getRatingClass(user.rating).toLowerCase()}`}>
            {user.rating}
          </span>
        )}
        {badge}
      </>
    );
    /*
     * La riga del selettore: una porta sola, larga quanto la riga.
     *
     * Si riusa `--link` per il reset — un bottone che non sembra un bottone — e
     * `--pick` aggiunge solo la geometria: lo spazio che era del bottone «Sfida»
     * piu' il padding del `<li>`, cosi' si preme davvero la linea intera e non
     * soltanto il nome. La coda a destra dice cosa succede: il chevron (premimi)
     * finche' non e' scelto, la spunta quando lo e'. Senza, tolto il bottone, la
     * riga non avrebbe piu' niente che prometta un gesto — e sul telefono non
     * esiste nessun `:hover` a suggerirlo.
     */
    if (sceglibile) {
      const scelto = selectedUserId === user.userId;
      return (
        <button
          type="button"
          className="player-info player-info--link player-info--pick"
          title={user.username}
          aria-pressed={scelto}
          onClick={() => onChallengePlayer(user)}
        >
          {contenutoInfo}
          {scelto
            ? <span className="player-selected-badge">&#x2713;</span>
            : <ChevronRight size={16} className="player-pick-chevron" aria-hidden="true" />}
        </button>
      );
    }
    /*
     * Stessa classe base sui due rami, come `button.game-row__who` e il suo
     * ripiego: la geometria non si muove fra il caso con profilo e quello senza.
     */
    return apreProfilo ? (
      <button
        type="button"
        className="player-info player-info--link"
        title={user.username}
        onClick={() => onViewProfile!(user)}
      >
        {contenutoInfo}
      </button>
    ) : (
      <div className="player-info">{contenutoInfo}</div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="online-players-list">
        <p className="login-required">{t('online.challenge.loginRequired')}</p>
      </div>
    );
  }

  /*
   * Il riquadro quando non c'e' nessun elenco in cui infilarlo.
   *
   * I quattro rami qui sotto — presenza scaduta, primo caricamento, guasto a
   * lista vuota, lista vuota — non disegnano nessun `<ul>`, e un riquadro aperto
   * che svanisse si porterebbe via il messaggio che spiega perche' un «Siediti»
   * e' stato rifiutato: e' proprio il motivo per cui questo riquadro, per tutto
   * il tempo in cui e' stato in cima al pannello, era al sicuro per costruzione.
   * Un `<div>` e non un `<li>`: qui non c'e' nessuna lista di cui essere voce.
   */
  const riquadroSenzaElenco = rigaEspansa && (
    <div className="riga-scelta">{rigaEspansa.contenuto}</div>
  );

  /*
   * Il conto alla rovescia, una frase sola e due vesti.
   *
   * La frase e' la stessa di sempre. Quella lunga la disegnano i rami qui sotto,
   * dove sta da sola in una riga sua o in coda a un avviso. Quella compatta —
   * l'icona della ricarica e i soli secondi — e' per la testata del pannello che
   * ospita la lista, dove accanto a un titolo la frase intera non ci sta: a 769px
   * la mezza colonna offre ~327px, e «Aktualisierung in 30 s» ne vuole 132 solo
   * per se'. Il testo intero resta leggibile in un `.player-sr` accanto, che e' la
   * ricetta con cui questa lista dice le cose che non disegna; niente `title` e
   * niente `aria-label`, che accanto a quello si leggerebbero due volte.
   *
   * Il portale sta qui e non fra `liScelta` e il `return`: il commento di
   * `liScelta` spiega che un test legge una finestra di caratteri a partire da
   * la'. Va ripetuto in ognuno dei rami qui sotto, per la stessa ragione del
   * riquadro qui sopra — un ramo che non lo rendesse spegnerebbe il conto nella
   * testata del pannello, che e' l'unico posto dove ormai si vede.
   */
  const frasePerIlConto = t('online.reloadIn', { seconds: secondsUntilReload });
  const contoLungo = (
    <span className="reload-countdown">{frasePerIlConto}</span>
  );
  const contoAltrove = testataAltrove?.conto
    ? createPortal(
      <span className="reload-countdown">
        <RefreshCw size={13} aria-hidden="true" />
        <span aria-hidden="true">{t('online.reloadInShort', { seconds: secondsUntilReload })}</span>
        <span className="player-sr">{frasePerIlConto}</span>
      </span>,
      testataAltrove.conto,
    )
    : null;

  /*
   * La presenza e' scaduta: qui non c'e' una lista vuota, c'e' una lista che non
   * esiste. Prima di questo ramo si vedevano i collegati dell'ultima
   * connessione, a tempo indeterminato — e un telefono fuori rete li mostrava
   * come se fossero li'.
   *
   * STA PRIMA di `isLoading`, e l'ordine e' la sostanza: a socket giu' il giro
   * di ricarica parte e fallisce subito (`NOT_CONNECTED`), quindi con l'ordine
   * invertito si vedrebbe lampeggiare lo spinner al posto dell'avviso. E sta
   * DOPO `isAuthenticated`, perche' a chi non e' entrato la lista non e'
   * promessa comunque.
   *
   * Due testi per due fatti diversi, e nessuna chiave nuova: il dispositivo
   * dichiara di non avere rete (lo si sa senza chiedere niente a nessuno),
   * oppure si crede online e il socket non c'e' — portale captive, server giu'.
   * Il secondo caso e' quello che diceva gia' `online.connecting`, e resta suo.
   */
  if (presenzaScaduta) {
    return (
      <div className="online-players-list" data-testid="lista-collegati-avviso">
        {contoAltrove}
        {riquadroSenzaElenco}
        {senzaRete ? (
          /* L'avviso che l'app ha gia' per questo identico fatto: titolo e corpo
             su due righe, l'icona che a 360px si toglie da sola, e la stessa
             famiglia visiva di `ConnessionePersaBanner`. Scriverlo a mano qui
             voleva dire due frasi incollate in una riga sola. */
          <AvvisoSenzaRete contesto="dati" />
        ) : (
          <p className="reconnect-message" role="status">{t('online.connecting')}</p>
        )}
      </div>
    );
  }

  /*
   * Lo spinner e' di chi non ha ancora NIENTE da mostrare.
   *
   * `caricaUtenti` alza `isLoading` prima dell'`await`, quindi senza la seconda
   * condizione ogni giro di ricarica sostituiva l'elenco intero con lo spinner
   * per il tempo di un viaggio di rete — e i giri non sono solo quello da trenta
   * secondi: anche il ritorno del socket, la presenza tornata valida, il cambio
   * di bucket e il timbro mancante un secondo e mezzo dopo l'arrivo di QUALUNQUE
   * registrato, che e' il piu' frequente di tutti. Le righe sbattevano fuori e
   * rientravano, e con loro si accartocciava l'altezza del foglio.
   *
   * E' la stessa regola che questo file applica gia' al guasto poco piu' sotto:
   * «la lista gia' caricata NON sparisce». Con lei questo ramo diventa quasi
   * irraggiungibile mentre una scelta e' aperta — se c'e' una scelta c'e' almeno
   * una riga — ma il riquadro ci sta comunque: lista svuotata da un annuncio,
   * ramo vuoto, giro di ricarica successivo, e ci si passa.
   */
  if (isLoading && onlineUsers.length === 0) {
    return (
      <div className="online-players-list">
        {contoAltrove}
        {riquadroSenzaElenco}
        <div className="spinner" />
        <p>{t('online.loadingPlayers')}</p>
      </div>
    );
  }

  /*
   * «Mi sto ricollegando» non e' «e' andato storto qualcosa».
   *
   * A socket giu' (o con l'ack che non torna) non c'e' nessun guasto da
   * annunciare: c'e' una connessione che sta tornando, in uno-tre secondi. Dirlo
   * come errore — per giunta in inglese, col `message` dei log — e' la ragione
   * per cui una PWA che si risveglia sembrava rotta.
   */
  const staRicollegando = codiceGuasto === 'NOT_CONNECTED' || codiceGuasto === 'TIMEOUT';
  const avviso = codiceGuasto === null
    ? null
    : (
      <p className={staRicollegando ? 'reconnect-message' : 'error-message'} role="status">
        {staRicollegando ? t('online.connecting') : t(chiaveErroreOnline(codiceGuasto))}
        {/* Il conto alla rovescia c'era gia', tradotto in sette lingue, e
            mancava proprio qui: nei secondi in cui serve di piu' non c'era
            nessun indizio che qualcosa sarebbe ripartito.
            Non quando la testata del pannello lo porta gia': sarebbero due
            orologi che dicono la stessa ora, a due centimetri di distanza. */}
        {!testataAltrove && <>{' '}{contoLungo}</>}
      </p>
    );

  /* La lista gia' caricata NON sparisce per un guasto: l'avviso le sta sopra.
     Da sola la frase resta solo quando non c'e' ancora niente da mostrare. */
  if (codiceGuasto !== null && onlineUsers.length === 0) {
    return (
      <div className="online-players-list" data-testid="lista-collegati-avviso">
        {contoAltrove}
        {riquadroSenzaElenco}
        {avviso}
      </div>
    );
  }

  if (onlineUsers.length === 0) {
    return (
      <div className="online-players-list">
        {contoAltrove}
        {riquadroSenzaElenco}
        <p className="no-players">{t('online.noPlayersOnline')}</p>
        {!testataAltrove && (
          <p className="reload-countdown">{frasePerIlConto}</p>
        )}
      </div>
    );
  }

  /*
   * La voce dell'elenco che E' il riquadro, e una sola per tutte le sue
   * posizioni: una `key` stabile dentro un array solo — quello che il `flatMap`
   * qui sotto appiattisce — vuol dire che il riquadro si SPOSTA invece di
   * rinascere. Due blocchi JSX
   * fratelli sarebbero due posti diversi, e React lo rimonterebbe anche a `key`
   * uguale — portandosi dietro il fuoco di chi ci stava dentro e un `role=alert`
   * che si riannuncia.
   *
   * Sta qui, appena prima del `return`, e non e' una comodita': il test
   * `nome-apre-il-profilo.test.ts` legge questo file dal disco e sorveglia che
   * una voce dell'elenco non apra nessun gestore di click entro i duecento
   * caratteri che la seguono («la riga non e' piu' un bersaglio»). Da questo
   * punto il primo gestore e' quello del bottone «Guarda», qualche migliaio di
   * caratteri piu' in la'. Vale anche per i commenti, che quel test legge come
   * codice: questo e' il motivo per cui qui sopra non si nominano le due cose
   * vicine fra loro.
   */
  const liScelta = rigaEspansa && (
    <li key="scelta" className="riga-scelta">{rigaEspansa.contenuto}</li>
  );
  /* Chi ospita il riquadro: la riga di quel giocatore, oppure la riga unita che
     lo contiene (ci finisce quando comincia a giocare, cioe' proprio quando gli
     si e' preso il tavolo da sotto). Se nessuno lo ospita, apre l'elenco. */
  const ospitata = idEspansa !== null && righe.some((riga) => (
    riga.kind === 'pair'
      ? riga.a.userId === idEspansa || riga.b.userId === idEspansa
      : riga.user.userId === idEspansa
  ));
  const orfano = Boolean(liScelta) && !ospitata;

  /*
   * Di quale punteggio si parla. Un numero nudo accanto a un nome non dice a quale
   * variante ne' a quale cadenza appartenga, e per otto punteggi per giocatore e'
   * un'informazione che manca. Si disegna solo se il server ha DAVVERO onorato il
   * bucket: se ha ripiegato, il chip racconterebbe una colonna che nessuno ha letto.
   *
   * Una costante e non JSX in linea perche' ha due case: dentro la testata della
   * lista, oppure — quando la testata la disegna l'ospite (`testataAltrove`) — da
   * solo, come riga sopra l'elenco. Nella testata dell'ospite non ci sta: le due
   * testate della coppia affiancata sono alte uguali per contratto, e una riga
   * intera in piu' sfalserebbe il filo che le attraversa.
   *
   * Non nel selettore: dentro Sfida diretta variante e cadenza si scelgono due menu
   * piu' sotto, nello stesso foglio, e questo chip le ripeteva.
   */
  const chipPunteggi = !selectionMode && bucketMostrato && (
    <div className={testataAltrove ? 'players-rating-bucket players-rating-bucket--sola' : 'players-rating-bucket'}>
      <span className="rating-bucket-label">{t('online.ratingsBucket')}</span>
      <span className="rating-bucket-value">
        {variantPill(bucketMostrato.variant).flag}
        {variantShortLabel(t, bucketMostrato.variant)}
        <span className="rating-bucket-sep" aria-hidden="true">·</span>
        {cadenceIcon(bucketMostrato.cadence, 12)}
        {t(`leaderboard.${bucketMostrato.cadence}`)}
      </span>
    </div>
  );

  return (
    <div className="online-players-list" ref={radiceRef}>
      {contoAltrove}
      {avviso}
      {/* La testata della lista non si disegna quando la disegna il pannello che
          la ospita: vedi `testataAltrove`. Si avvolge l'involucro INTERO e non i
          suoi figli uno per uno — col badge del totale gia' spento da `hideCount`
          non resterebbe dentro che il titolino, che quella testata ripete, e una
          riga quasi vuota col suo filo e il suo margine si vede.
          Il chip dei punteggi invece resta, e si sposta: fuori dall'involucro e'
          una riga sua sopra l'elenco. Dichiara QUALE colonna portano i numeri
          delle righe, e da affiancati quella colonna la comanda il menu «Gioca» di
          fianco — cioe' cambia proprio mentre si guarda. */}
      {!testataAltrove ? (
        <div className="players-header">
          {/* Cosa farci, non cosa sia: la lista si intitolava «Giocatori
              collegati» in tutti e due i posti dove vive, ripetendo il titolo del
              pannello che la ospita invece di dire che le righe si cliccano. */}
          <h3>{t('online.chooseOpponent')}</h3>
          <div className="players-header-meta">
            {contoLungo}
            {!hideCount && <span className="player-count">{onlineUsers.length}</span>}
          </div>
          {chipPunteggi}
        </div>
      ) : chipPunteggi}
      <ul className="players-list">
        {righe.flatMap((riga, indice) => {
          /* Il riquadro orfano apre l'elenco. Dentro lo stesso `flatMap` e non in
             un blocco JSX accanto: un array solo, una `key` sola, nessun
             rimontaggio quando la riga che lo ospitava torna. */
          const testa = indice === 0 && orfano ? [liScelta] : [];
          /*
           * La riga unita: una partita, una riga.
           *
           * Due porte invece di una, il «vs» in mezzo, UNA pastiglia che dice su
           * cosa si gioca (non piu' «con chi» per due volte) e UN solo «Guarda»,
           * che prima erano due bottoni per la stessa scacchiera.
           *
           * Si entra sempre dal lato di `a`: la damiera dello spettatore si gira,
           * quindi l'altra prospettiva non si perde.
           */
          if (riga.kind === 'pair') {
            const { a, b } = riga;
            /* Difensivo: un giocatore in partita non e' selezionabile — la sua
               riga non offre «Sfida» — ma uno scelto PRIMA puo' essersi messo a
               giocare nel frattempo. */
            const isSelected = selectedUserId === a.userId || selectedUserId === b.userId;
            /* Subito DOPO la coppia, non in cima: un `TABLE_TAKEN` significa che
               l'host si e' messo a giocare, e se anche chi gli ha preso il tavolo
               e' in lista `righeOnline` li fonde in una riga sola. Mandare il
               riquadro in testa proprio li' vorrebbe dire rifare il difetto che
               questo lavoro e' venuto a chiudere. */
            const coda = idEspansa !== null && (a.userId === idEspansa || b.userId === idEspansa)
              ? [liScelta]
              : [];
            return [...testa, (
              <li
                key={a.userId}
                className={`player-item player-item--pair ${isSelected ? 'selected' : ''} ${idLampo === a.userId ? 'player-item--lampo' : ''}`}
                onClick={(e) => indicaAzione(e, a.userId)}
              >
                {/*
                  * Una scheda d'incontro impilata: giocatore, «vs» con la
                  * pastiglia della partita, giocatore. La disposizione la fa una
                  * GRIGLIA (vedi il foglio) e non un flex, perche' flex decide
                  * dove rompere le linee sulle dimensioni di CONTENUTO e solo
                  * dopo stringe: i due nomi finivano su linee diverse col «vs»
                  * appeso in fondo alla prima, e tenerli insieme costava un
                  * `nowrap` dentro un secondo contenitore.
                  *
                  * Gli involucri `__slot` servono a una cosa sola: due elementi
                  * con le STESSE classi devono finire in due aree di griglia
                  * diverse, e la className di `portaProfilo` e' letterale
                  * (`nome-apre-il-profilo.test.ts` la legge dal disco), quindi
                  * non la si puo' rendere dinamica. Un `:nth-child()` farebbe lo
                  * stesso ed e' piu' fragile.
                  *
                  * L'ordine nel DOM e' la frase: A, «vs», B, pastiglia, azioni —
                  * «Ada, vs, Bo, Italiana 8x8, Blitz, Guarda». La griglia alza la
                  * pastiglia accanto al «vs», e puo' farlo perche' quella NON e'
                  * focalizzabile: la tabulazione resta porta A -> porta B ->
                  * Guarda, cioe' i due giocatori e poi l'azione.
                  */}
                <div className="player-pair__slot player-pair__slot--a">
                  {portaProfilo(a, null)}
                </div>
                <span className="player-pair__vs">{t('common.vs')}</span>
                <div className="player-pair__slot player-pair__slot--b">
                  {portaProfilo(b, null)}
                </div>
                {/* Da una delle due righe, quella che ce l'ha: con un rientro a
                    meta' una sola delle due torna dalla lista. */}
                {badgePartita(a.game ?? b.game)}
                <div className="player-actions">
                  {onSpectatePlayer ? (
                    <button
                      type="button"
                      className="player-watch-btn"
                      aria-label={t('spectate.watchPair', { a: a.username, b: b.username })}
                      onClick={() => onSpectatePlayer(a)}
                    >
                      <Eye size={16} aria-hidden="true" />
                      <span>{t('spectate.watch')}</span>
                    </button>
                  ) : (
                    /* Dentro Sfida diretta non si guarda nessuno: un'etichetta
                       inerte per la coppia, non due. */
                    <span className="player-busy">{t('online.status.busy')}</span>
                  )}
                </div>
              </li>
            ), ...coda];
          }

          const user = riga.user;
          /* Al posto della riga, non sopra ne' sotto: la risposta arriva dove e'
             stato fatto il gesto. Il nome del giocatore lo dice il titolo del
             riquadro, quindi non si perde chi sia. */
          if (user.userId === idEspansa) return [...testa, liScelta];
          const isSelected = selectedUserId === user.userId;
          return [...testa, (
            <li
              key={user.userId}
              className={`player-item ${isSelected ? 'selected' : ''} ${idLampo === user.userId ? 'player-item--lampo' : ''}`}
              onClick={(e) => indicaAzione(e, user.userId)}
            >
              {/*
                * Il blocco di sinistra — avatar, nome, punteggio e stato — e' UN
                * bersaglio solo, e cosa faccia dipende da dove vive la lista: nel
                * pannello dei collegati apre il profilo (negli altri nove posti
                * dell'app si fa cosi'), nel selettore sceglie quell'avversario e
                * si prende tutta la riga. Mai tutte e due. Vedi `portaProfilo`.
                */}
              {portaProfilo(user, getStatusBadge(user, tavoloDi(tavoliPerHost, user.username)))}
              {/* Nel selettore una riga libera non ha azioni: l'azione E' la riga,
                  e la spunta della scelta sta in fondo alla porta. Restano le righe
                  che selettore o no non si scelgono — «Guarda» e «Occupato». */}
              {!rigaSceglie(user) && (
                <div className="player-actions">
                  {/* A busy player used to be a dead end. Now their game can be
                      watched — but only when they are genuinely playing: an 'away'
                      player also reaches this branch and has no game to show.
                      Una riga `playing` arriva qui solo quando la coppia NON si e'
                      formata: l'avversario non e' in lista (sta giocando con chi
                      guarda, che la lista esclude) o il suo annuncio e' arrivato a
                      meta'. */}
                  {user.status === 'playing' && onSpectatePlayer ? (
                    <button
                      type="button"
                      className="player-watch-btn"
                      aria-label={t('spectate.watchPlayer', { username: user.username })}
                      onClick={() => onSpectatePlayer(user)}
                    >
                      <Eye size={16} aria-hidden="true" />
                      <span>{t('spectate.watch')}</span>
                    </button>
                  ) : user.status !== 'available' ? (
                    /* Parola SUA e non quella del badge: qui siamo nella colonna
                       dei bottoni, e in italiano il badge adesso dice «Gioca» —
                       che li' si leggerebbe come un invito a premere. Non e' un
                       ramo morto: dentro Sfida diretta `onSpectatePlayer` non
                       arriva, e ogni riga in partita passa di qua. */
                    <span className="player-busy">{t('online.status.busy')}</span>
                  ) : isSelected ? (
                    <span className="player-selected-badge">&#x2713;</span>
                  ) : (
                    /*
                     * La sfida ha un BOTTONE, e ormai SOLO il bottone.
                     *
                     * Per molto tempo l'unico modo di sfidare era l'`onClick` sul
                     * `<li>`: un elemento che non entra nel flusso di tabulazione,
                     * non ha ruolo e non risponde a Invio — chi navigava da tastiera
                     * vedeva l'elenco dei disponibili e non poteva sfidare nessuno.
                     * Questo bottone e' nato per quello, e la riga era rimasta
                     * cliccabile accanto a lui «perche' col dito il bersaglio grande
                     * e' quello giusto».
                     *
                     * Quel bersaglio grande adesso ce l'ha l'ALTRA azione: il blocco
                     * di sinistra apre il profilo, ed e' il pezzo di riga che si
                     * preme davvero quando si guarda un nome. Tenere anche la riga
                     * voleva dire che lo stesso gesto, a due dita di distanza, faceva
                     * due cose diverse — e che il `cursor: pointer` prometteva una
                     * sfida sopra due terzi di riga che aprivano un profilo.
                     * Il prezzo e' dichiarato: da telefono si sfida da qui, 44x44.
                     *
                     * E dentro il selettore questo bottone non esiste proprio: li' non
                     * sfidava nessuno — sceglieva — e la riga intera fa quel lavoro
                     * meglio di lui. Vedi `rigaSceglie`.
                     */
                    <button
                      type="button"
                      className="player-challenge-btn"
                      /* La maniglia con cui il fuoco torna qui quando la scelta
                         si chiude: vedi l'effetto in testa al componente. */
                      data-userid={user.userId}
                      aria-label={t('online.challenge.challengePlayer', { username: user.username })}
                      onClick={() => onChallengePlayer(user)}
                    >
                      <Swords size={16} aria-hidden="true" />
                      <span>{t('online.challenge.challenge')}</span>
                    </button>
                  )}
                </div>
              )}
            </li>
          )];
        })}
      </ul>
    </div>
  );
}

export default OnlinePlayersList;
